import { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isValid,
  isSubmitting,
  hasSubmitSucceeded,
  getFormSyncErrors,
  submit,
  destroy
} from 'redux-form';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { RadioExportFormFields } from '../prefabs/RadioExportForm';
import { SelectionItemInterface } from '@cube3/state/src/redux/ducks/selections';

export const generateRadioFormId = (assetId: string) => {
  return `radio_export_form__${assetId}`;
};

export const useRadioBroadcastMetadataForms = (
  assets: SelectionItemInterface[],
  exportTargetId: string
) => {
  const dispatch = useDispatch();

  const forms = useMemo(() => {
    return assets.map((asset) => ({
      ...asset,
      form: generateRadioFormId(asset.id)
    }));
  }, [assets]);

  const allFormValues = useTypedSelector((state) => {
    const form = state.form;
    const val = forms.map((asset) => {
      const values = form[asset.form]?.values as RadioExportFormFields;
      if (values) {
        return {
          parameters: {
            medialab: {
              title: values.title,
              commercial_length: values.commercial_length,
              advertiser: values.client,
              production_company: values.product_service,
              stations: values.broadcasters,
              // broadcasters: values.broadcasters,
              first_broadcast_date: values.first_broadcast_date
                ? (() => {
                    const date = new Date(values.first_broadcast_date);

                    return new Date(
                      Date.UTC(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      )
                    ).toISOString();
                  })()
                : new Date().toISOString()
            }
          },
          asset_id: asset.id,
          target_id: exportTargetId
        };
      }
      return null;
    });
    return val.filter(Boolean);
  });

  const allFormsValid = useSelector((state) => {
    return forms.reduce(
      (acc, form) => {
        const valid = isValid(form.form)(state);

        return {
          ...acc,
          validForms: valid ? [...acc.validForms, form.id] : acc.validForms,
          allValid: !valid ? false : acc.allValid
        };
      },
      { validForms: [] as string[], allValid: true }
    );
  });

  const formSubmitting = useSelector((state) => {
    return forms.reduce(
      (acc, form) => {
        const submitting = isSubmitting(form.form)(state);

        return {
          ...acc,
          [form.id]: submitting,
          anySubmitting: acc.anySubmitting ? true : submitting
        };
      },
      { anySubmitting: false }
    );
  });

  const { allSucceeded: allFormsSucceeded, anySucceeded: anyFormsSucceeded } =
    useSelector((state) => {
      return forms.reduce(
        (acc, form) => {
          const success = hasSubmitSucceeded(form.form)(state);

          return {
            ...acc,
            [form.id]: success,
            anySucceeded: acc.anySucceeded ? true : success,
            allSucceeded: !success ? false : acc.allSucceeded
          };
        },
        { allSucceeded: true, anySucceeded: false }
      );
    });

  const allFormErrors = useSelector((state) => {
    return forms.reduce(
      (acc, form) => {
        const errors = getFormSyncErrors(form.form)(state);
        return {
          ...acc,
          errors: { ...acc.errors, [form.id]: errors },
          hasErrors: Object.keys(errors).length ? true : acc.hasErrors
        };
      },
      { hasErrors: false, errors: {} }
    );
  });

  /** submit all forms except the first export, because it has been created seperately */
  const submitAllForms = useCallback(() => {
    forms.forEach((asset) => {
      if (allFormsValid.allValid) {
        dispatch(submit(asset.form));
      }
    });
  }, [forms, allFormsValid.allValid, dispatch]);

  /** Maps over the retrieved forms and dispatches a destroy event per form */
  const destroyAllForms = useCallback(() => {
    forms.forEach((form) => {
      requestAnimationFrame(() => {
        dispatch(destroy(form.form));
      });
    });
  }, [dispatch, forms]);

  return {
    allFormsValid,
    submitAllForms,
    allFormsSucceeded: allFormsSucceeded && anyFormsSucceeded,
    formSubmitting: formSubmitting.anySubmitting,
    destroyAllForms,
    allFormErrors,
    allFormValues
  };
};
